import { Controller } from '@hotwired/stimulus'
import { Chart, registerables } from 'chart.js'

// Register all Chart.js components
Chart.register(...registerables)

/**
 * Analytics Funnel Chart Controller
 *
 * Displays a horizontal bar chart showing users at each funnel stage
 */
export default class extends Controller {
  static values = {
    url: String
  }

  connect() {
    this.loadChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  async loadChart() {
    try {
      const response = await fetch(this.urlValue)
      const data = await response.json()

      // Hide loading, show canvas
      const loadingDiv = this.element.querySelector('div')
      const canvas = this.element.querySelector('canvas')

      if (loadingDiv) loadingDiv.style.display = 'none'
      if (canvas) canvas.style.display = 'block'

      const ctx = canvas.getContext('2d')

      // Color palette
      const colors = [
        'rgba(59, 130, 246, 0.8)', // blue
        'rgba(16, 185, 129, 0.8)', // green
        'rgba(245, 158, 11, 0.8)', // amber
        'rgba(139, 92, 246, 0.8)', // purple
        'rgba(236, 72, 153, 0.8)', // pink
        'rgba(20, 184, 166, 0.8)', // teal
        'rgba(99, 102, 241, 0.8)', // indigo
        'rgba(244, 63, 94, 0.8)', // rose
        'rgba(132, 204, 22, 0.8)', // lime
        'rgba(6, 182, 212, 0.8)' // cyan
      ]

      this.chart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: data.stages.map((s) => s.label),
          datasets: [
            {
              label: 'Users in Stage',
              data: data.stages.map((s) => s.count),
              backgroundColor: data.stages.map((_, i) => colors[i % colors.length]),
              borderRadius: 6,
              barThickness: 30
            }
          ]
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                afterLabel: (context) => {
                  const idx = context.dataIndex
                  if (idx < data.conversions.length) {
                    return `Conversion to next: ${data.conversions[idx].rate}%`
                  }
                  return ''
                }
              }
            }
          },
          scales: {
            x: {
              beginAtZero: true,
              grid: { color: 'rgba(0,0,0,0.05)' }
            },
            y: {
              grid: { display: false }
            }
          }
        }
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[AnalyticsFunnel] Error loading chart:', error)
      const loadingDiv = this.element.querySelector('div')
      if (loadingDiv) {
        loadingDiv.textContent = 'Failed to load chart'
        loadingDiv.style.color = 'var(--color-error-600)'
      }
    }
  }
}
